'use client';

import React, { useMemo, useState } from 'react';

import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { Card } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useGetVolumeByLocationQuery } from 'src/api/redux';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import { buildTopProvidersQueryParams } from 'src/components/Profiles/shared/widgets/CompactTopProviders/utils';
import CompactLocationByDay from './CompactLocationByDay';
import CompactLocationRowItem from './CompactLocationRowItem';

const MAX_ITEMS = 10;
const TOP_LOCATION_THRESHOLD = 20;

const CompactVolumeByLocation: React.FC = () => {
  const { entityDetails } = useProfile();
  const [expanded, setExpanded] = useState<string[]>([]);

  const { providerId, profileType } = getEntityIdentifiers(entityDetails);

  const widgetConfig = useWidgetConfig();
  const currentSavedSearch = widgetConfig?.filterSet?.label
    ? `- ${widgetConfig.filterSet.label} Claims`
    : '- All Claims';

  const filterSet = widgetConfig?.filterSet ?? null;
  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;
  const providerFilterSetId = widgetConfig?.providerFilterSetId;
  const encounterId = widgetConfig?.widget?.encounter?.id ?? null;

  const queryParams = useMemo(
    () => buildTopProvidersQueryParams(filterSet, minYear, maxYear, providerFilterSetId, encounterId),
    [filterSet, minYear, maxYear, providerFilterSetId, encounterId]
  );

  const { data, isLoading } = useGetVolumeByLocationQuery(
    { providerId, queryParams },
    { skip: !providerId || !profileType }
  );

  const topLocations = useMemo(() => {
    if (!data?.results) return [];
    return data.results
      .filter(
        (loc: Record<string, any>) =>
          loc.provider_id && loc.percentage >= TOP_LOCATION_THRESHOLD
      )
      .slice(0, MAX_ITEMS);
  }, [data]);

  const otherLocations = useMemo(() => {
    if (!data?.results) return [];
    const lowVolume = data.results
      .filter(
        (loc: Record<string, any>) =>
          loc.provider_id && loc.percentage < TOP_LOCATION_THRESHOLD
      )
      .slice(0, MAX_ITEMS);
    const nullEntry = data.results.find(
      (loc: Record<string, any>) => !loc.provider_id
    );
    if (nullEntry) lowVolume.push(nullEntry);
    return lowVolume;
  }, [data]);

  const hasData = topLocations.length > 0 || otherLocations.length > 0;

  return (
    <Card
      className="p-4 flex flex-col gap-2 bg-white h-full"
      data-testid="compact-volume-by-location"
    >
      <WidgetHeader fallbackTitle={`Top Locations ${currentSavedSearch}`} />

      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          <WidgetLoading data-testid="compact-volume-by-location-loading" />
        ) : !hasData ? (
          <WidgetEmpty data-testid="compact-volume-by-location-empty" variant="full" />
        ) : (
          <div className="flex flex-col gap-2">
            {/* Top locations with accordion */}
            {topLocations.length > 0 && (
              <div
                className="flex flex-col"
                role="list"
                aria-label="Top Locations"
                data-testid="compact-volume-by-location-list"
              >
                {topLocations.map((loc: Record<string, any>, index: number) => (
                  <CompactLocationByDay
                    key={`${loc.provider_id}-${index}`}
                    isFirst={index === 0}
                    location={loc}
                    expanded={expanded}
                    setExpanded={setExpanded}
                  />
                ))}
              </div>
            )}

            {/* Other locations table */}
            {otherLocations.length > 0 && (
              <div data-testid="compact-volume-by-location-other">
                <div className="flex items-center justify-between bg-neutral-100 px-4 py-2">
                  <span className="text-sm font-bold leading-6 text-neutral-900">
                    Other Locations
                  </span>
                  <span className="text-sm font-bold leading-6">
                    % of Total
                  </span>
                </div>
                <div role="list" aria-label="Other Locations">
                  {otherLocations.map(
                    (loc: Record<string, any>, index: number) => (
                      <CompactLocationRowItem
                        key={`other-${loc.provider_id ?? 'null'}-${index}`}
                        location={loc}
                      />
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="py-0.5">
        <span className="text-xs text-neutral-500 font-normal">
          Prescription data not available in this widget.
        </span>
      </div>
    </Card>
  );
};

export default CompactVolumeByLocation;
